import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articlesContent } from '@/mocks/siteData';
import type { ArticleSection, ArticleFAQ } from '@/mocks/siteData';

const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://superbuyspread.bond';

function ArticleNotFound() {
  return (
    <div className="max-w-4xl mx-auto text-center py-32 px-6">
      <h1 className="font-heading text-3xl font-semibold text-foreground-950">Article Not Found</h1>
      <p className="mt-4 text-foreground-500">The article you are looking for does not exist or has been moved.</p>
      <Link to="/" className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">
        <i className="ri-arrow-left-line"></i>
        Back to Superbuy Spreadsheet
      </Link>
    </div>
  );
}

function renderBody(section: ArticleSection, idx: number) {
  if (section.type === 'h2') {
    return (
      <h2 key={idx} className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-10 mb-4">
        {section.text}
      </h2>
    );
  }
  if (section.type === 'h3') {
    return (
      <h3 key={idx} className="font-heading text-xl font-semibold text-foreground-950 mt-8 mb-3">
        {section.text}
      </h3>
    );
  }
  return (
    <p key={idx} className="text-base text-foreground-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: section.text }} />
  );
}

function makeArticleJsonLd(slug: string, meta: { title: string; subtitle: string; heroImage: string; date: string; updated: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": meta.title,
    "description": meta.subtitle,
    "image": meta.heroImage,
    "datePublished": meta.date,
    "dateModified": meta.updated,
    "author": {
      "@type": "Person",
      "name": "Superbuy Spreadsheet Editorial Team"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Superbuy Spreadsheet",
      "url": siteUrl
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${siteUrl}/article/${slug}`
    }
  };
}

function makeFaqJsonLd(faq: ArticleFAQ[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faq.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": f.a
      }
    }))
  };
}

function makeBreadcrumbJsonLd(slug: string, title: string) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Superbuy Spreadsheet",
        "item": siteUrl
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": title,
        "item": `${siteUrl}/article/${slug}`
      }
    ]
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articlesContent[slug] : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-[72px]"><ArticleNotFound /></main>
        <Footer />
      </>
    );
  }

  const { meta, quickAnswer, takeaways, pros, cons, body, comparisonTable, checklistItems, faq, expertInsight, relatedArticles } = article;

  const articleJsonLd = makeArticleJsonLd(slug!, meta);
  const faqJsonLd = makeFaqJsonLd(faq);
  const breadcrumbJsonLd = makeBreadcrumbJsonLd(slug!, meta.title);

  return (
    <>
      <Navbar />
      <main className="pt-[72px]">
        <article>
          {/* Breadcrumb + Hero */}
          <section className="py-16 md:py-24 px-6 md:px-10 bg-background-50">
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-wrap items-center gap-2 mb-6">
                <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors font-medium">
                  Home
                </Link>
                <i className="ri-arrow-right-s-line text-foreground-300 text-sm"></i>
                <span className="text-sm text-foreground-400">Guides</span>
              </div>

              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-950 leading-tight">
                {meta.title}
              </h1>

              <p className="mt-4 text-base md:text-lg text-foreground-500 max-w-2xl">
                {meta.subtitle}
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-foreground-400">
                <span>{meta.date}</span>
                <span>·</span>
                <span>{meta.readTime}</span>
                <span>·</span>
                <span>Last Updated: {meta.updated}</span>
              </div>

              <div className="mt-8 rounded-3xl overflow-hidden aspect-[21/9]">
                <img
                  src={meta.heroImage}
                  alt={meta.heroAlt}
                  title={meta.heroTitle}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </section>

          {/* Quick Answer */}
          <section className="py-12 px-6 md:px-10 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <div className="bg-accent-100 border border-accent-200/50 rounded-2xl p-6 md:p-8">
                <h2 className="font-heading text-lg font-semibold text-accent-900 mb-3">Quick Answer</h2>
                <p className="text-sm text-accent-800 leading-relaxed">{quickAnswer}</p>
              </div>

              {/* Takeaways + Pros & Cons */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-background-50 border border-background-200/70 rounded-2xl p-6">
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-3 flex items-center gap-2">
                    <i className="ri-check-double-line text-accent-500"></i>
                    Key Takeaways
                  </h3>
                  <ul className="space-y-2 text-sm text-foreground-600">
                    {takeaways.map((item, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent-500 mt-1.5 flex-shrink-0"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-background-50 border border-background-200/70 rounded-2xl p-6">
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-3 flex items-center gap-2">
                    <i className="ri-lightbulb-line text-primary-500"></i>
                    Pros &amp; Cons
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div>
                      <p className="text-accent-700 font-medium mb-1">Pros</p>
                      <ul className="space-y-1 text-foreground-600">
                        {pros.map((item, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <i className="ri-add-line text-accent-500 text-xs mt-0.5"></i>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-foreground-700 font-medium mb-1">Cons</p>
                      <ul className="space-y-1 text-foreground-500">
                        {cons.map((item, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <i className="ri-subtract-line text-foreground-400 text-xs mt-0.5"></i>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Main Content Body */}
          <section className="py-16 px-6 md:px-10 bg-background-50">
            <div className="max-w-4xl mx-auto">
              <div className="space-y-5">
                {body.map((section, idx) => renderBody(section, idx))}
              </div>

              {/* Checklist (for QC article) */}
              {checklistItems && checklistItems.length > 0 && (
                <div className="mt-10 p-6 md:p-8 bg-accent-50 border border-accent-200/40 rounded-2xl">
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-checkbox-line text-accent-500"></i>
                    Complete QC Checklist
                  </h3>
                  <ul className="space-y-3">
                    {checklistItems.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-foreground-700">
                        <span className="w-6 h-6 rounded-full bg-accent-100 border border-accent-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-xs font-semibold text-accent-700">{i + 1}</span>
                        </span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Comparison Table (for shipping article) */}
              {comparisonTable && (
                <div className="mt-10">
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-sm">
                      <thead>
                        <tr className="border-b border-background-200">
                          {comparisonTable.headers.map((h, i) => (
                            <th key={i} className="text-left py-3 px-4 font-semibold text-foreground-800 whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="text-foreground-600">
                        {comparisonTable.rows.map((row, ri) => (
                          <tr key={ri} className="border-b border-background-100">
                            {row.map((cell, ci) => (
                              <td key={ci} className="py-3 px-4 whitespace-nowrap">{cell}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Category Reference Table (for beginner guide) */}
              {slug === 'superbuy-sheet-beginner-guide' && (
                <div className="overflow-x-auto mt-8">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="border-b border-background-200">
                        <th className="text-left py-3 px-4 font-semibold text-foreground-800">Category</th>
                        <th className="text-left py-3 px-4 font-semibold text-foreground-800">Items</th>
                        <th className="text-left py-3 px-4 font-semibold text-foreground-800">Best For</th>
                      </tr>
                    </thead>
                    <tbody className="text-foreground-600">
                      <tr className="border-b border-background-100"><td className="py-3 px-4 font-medium text-foreground-800">Shoes</td><td className="py-3 px-4">2,400+</td><td className="py-3 px-4">Sneakers, boots, casual shoes</td></tr>
                      <tr className="border-b border-background-100"><td className="py-3 px-4 font-medium text-foreground-800">Hoodies/Sweaters</td><td className="py-3 px-4">1,850+</td><td className="py-3 px-4">Streetwear, winter layers</td></tr>
                      <tr className="border-b border-background-100"><td className="py-3 px-4 font-medium text-foreground-800">T-Shirts</td><td className="py-3 px-4">3,200+</td><td className="py-3 px-4">Basics, graphic tees</td></tr>
                      <tr className="border-b border-background-100"><td className="py-3 px-4 font-medium text-foreground-800">Jackets</td><td className="py-3 px-4">1,100+</td><td className="py-3 px-4">Outerwear, bombers</td></tr>
                      <tr className="border-b border-background-100"><td className="py-3 px-4 font-medium text-foreground-800">Accessories</td><td className="py-3 px-4">1,950+</td><td className="py-3 px-4">Hats, jewelry, sunglasses</td></tr>
                      <tr><td className="py-3 px-4 font-medium text-foreground-800">Bags</td><td className="py-3 px-4">1,630+</td><td className="py-3 px-4">Backpacks, totes, crossbody</td></tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* FAQ */}
              {faq && faq.length > 0 && (
                <div className="mt-10">
                  <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-6">
                    Frequently Asked Questions
                  </h3>
                  <div className="space-y-4">
                    {faq.map((item: ArticleFAQ, i: number) => (
                      <div key={i}>
                        <h4 className="font-semibold text-foreground-900 mb-1">{item.q}</h4>
                        <p className="text-foreground-600 text-sm leading-relaxed">{item.a}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Expert Insight */}
              {expertInsight && (
                <div className="mt-10 p-6 bg-background-100 border border-background-200/70 rounded-2xl">
                  <h4 className="font-heading text-base font-semibold text-foreground-950 mb-2 flex items-center gap-2">
                    <i className="ri-sparkling-line text-primary-500"></i>
                    Expert Insight
                  </h4>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    &ldquo;{expertInsight}&rdquo;
                  </p>
                </div>
              )}
            </div>
          </section>

          {/* Related Articles */}
          <section className="py-16 px-6 md:px-10 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h3 className="font-heading text-2xl font-semibold text-foreground-950 mb-8">Related Reads</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {relatedArticles.map((ra) => (
                  <Link key={ra.slug} to={`/article/${ra.slug}`} className="group bg-background-50 border border-background-200/70 rounded-2xl p-6 hover:border-background-300/60 transition-all duration-300 cursor-pointer">
                    <h4 className="font-heading text-base font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors">
                      {ra.title}
                    </h4>
                    <p className="text-sm text-foreground-500 mt-2">
                      {ra.excerpt}
                    </p>
                  </Link>
                ))}
              </div>

              <div className="mt-10 pt-8 border-t border-background-200/70">
                <Link to="/" className="inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 transition-colors font-medium">
                  <i className="ri-arrow-left-line"></i>
                  Back to Superbuy Spreadsheet Homepage
                </Link>
              </div>
            </div>
          </section>
        </article>

        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}></script>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}></script>
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}></script>
      </main>
      <Footer />
    </>
  );
}